// Caches the OIDC discovery document (the "well-known") in localStorage.
//
// getInitializedUserManager fetches it on every app boot, and a cold login boots
// the app twice — once at "/" to discover there is no session, again at
// /auth/callback to exchange the code. That is two round trips to the identity
// provider before either document can do anything, for a file that changes
// approximately never. The silent renew frame pays for a third.
//
// Served stale-while-revalidate: inside FRESH_MS nothing touches the network at
// all; past that the cached copy is returned immediately and a refresh runs in
// the background, so a boot is never blocked by a document we already have.
// Past MAX_STALE_MS the cache is not trusted and the fetch blocks as before.

const STORAGE_KEY_PREFIX = "engage.oidc.metadata:";

const FRESH_MS = 12 * 60 * 60 * 1000;
const MAX_STALE_MS = 24 * 60 * 60 * 1000;

// Keyed by URL so an environment or tenant change reads a different entry rather
// than a stale one belonging to the previous provider. Switching between
// ForgeRock and Entra changes this URL, so that switch invalidates itself.
const storageKey = (url) => `${STORAGE_KEY_PREFIX}${url}`;

// Guards against trusting a cached error page. A discovery document without an
// issuer is not one, however well-formed the JSON is.
const isUsableMetadata = (config) =>
  !!config && typeof config === "object" && typeof config.issuer === "string";

const readEntry = (url) => {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(url));
    if (!raw) {
      return null;
    }

    const entry = JSON.parse(raw);
    if (
      typeof entry?.fetchedAt !== "number" ||
      !isUsableMetadata(entry.config)
    ) {
      return null;
    }

    return { config: entry.config, age: Date.now() - entry.fetchedAt };
  } catch {
    // Unreadable, unparseable, or storage denied — treat it as a cache miss.
    // Never let this path fail a login.
    return null;
  }
};

const writeEntry = (url, config) => {
  try {
    globalThis.localStorage?.setItem(
      storageKey(url),
      JSON.stringify({ fetchedAt: Date.now(), config }),
    );
  } catch {
    // Quota or a storage-denied context (private mode, sandboxed frame). The
    // caller already has its metadata; caching it is best effort.
  }
};

// Deduplicates concurrent callers within one document, so App and the silent
// renew frame cannot start the same request twice.
const inFlight = new Map();

const fetchMetadata = async (url) => {
  if (inFlight.has(url)) {
    return inFlight.get(url);
  }

  const request = (async () => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `OIDC discovery request failed with ${response.status} for ${url}`,
      );
    }

    const config = await response.json();
    if (!isUsableMetadata(config)) {
      throw new Error(`OIDC discovery document from ${url} has no issuer`);
    }

    writeEntry(url, config);
    return config;
  })();

  inFlight.set(url, request);

  try {
    return await request;
  } finally {
    inFlight.delete(url);
  }
};

export const getOidcMetadata = async (url) => {
  const cached = readEntry(url);

  if (cached && cached.age < FRESH_MS) {
    return cached.config;
  }

  if (cached && cached.age < MAX_STALE_MS) {
    // Return what we have and refresh behind it. The rejection is swallowed on
    // purpose: a failed background refresh just means the next boot revalidates
    // again, and an unhandled rejection here would surface as a console error on
    // a boot that actually succeeded.
    fetchMetadata(url).catch((error) => {
      console.warn("Background OIDC metadata refresh failed.", error);
    });
    return cached.config;
  }

  return fetchMetadata(url);
};

// No explicit invalidation hook on purpose. The two ways this entry could ever
// be wrong for the current provider are both already covered: a provider or
// environment change changes the URL and therefore the key, and logout runs
// resetLogoutBrowserState, which calls localStorage.clear(). Adding a third path
// would be dead code.
